import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'container',
    'template',
    'addButton',
    'assignButtonWrapper',
    'addButtonWrapper',
    'remapButtonWrapper',
    'actionsWrapper',
  ];

  static values = { descriptions: Object };

  connect() {
    this.captureInitialState();
    this.renderState();
    this.updateDescriptions();
    this.announceState();
  }

  add() {
    const fragment = this.templateTarget.content.cloneNode(true);
    this.containerTarget.appendChild(fragment);

    const rows = this.ruleRows();
    const newRow = rows[rows.length - 1];

    if (newRow) {
      const select = newRow.querySelector("select[name$='[kind]']");
      if (select) {
        const useSourceOptions = this.realRuleCountExcludingRow(newRow) === 0;
        this.replaceRuleOptions(select, useSourceOptions);
      }
    }

    this.refreshRuleKinds();
    this.renderState();
    this.updateDescriptions();
    this.announceState();
  }

  remove(event) {
    const row = event.target.closest('.rule-row');
    if (row) row.remove();

    this.refreshRuleKinds();
    this.renderState();
    this.updateDescriptions();
    this.announceState();
  }

  ruleKindChanged() {
    this.refreshRuleKinds();
    this.renderState();
    this.updateDescriptions();
    this.announceState();
  }

  ruleRows() {
    return Array.from(this.containerTarget.querySelectorAll('.rule-row'));
  }

  ruleSelects() {
    return Array.from(this.containerTarget.querySelectorAll("select[name$='[kind]']"));
  }

  realRuleCount() {
    return this.ruleSelects().filter((select) => {
      return select.value && select.value !== 'NONE';
    }).length;
  }

  realRuleCountExcludingRow(excludedRow) {
    return this.ruleRows().filter((row) => {
      if (row === excludedRow) return false;
      const select = row.querySelector("select[name$='[kind]']");
      return select && select.value && select.value !== 'NONE';
    }).length;
  }

  firstRuleSelected() {
    const firstRow = this.ruleRows()[0];
    if (!firstRow) return false;

    const select = firstRow.querySelector("select[name$='[kind]']");
    return !!(select && select.value && select.value !== 'NONE');
  }

  captureInitialState() {
    this.initialSnapshot = this.serializeRuleInputs();
  }

  isDirty() {
    return this.serializeRuleInputs() !== this.initialSnapshot;
  }

  renderState() {
    const hasAnyRows = this.ruleRows().length > 0;
    const firstRuleSelected = this.firstRuleSelected();
    const showActions = firstRuleSelected;
    const showRemap = firstRuleSelected && this.isDirty();

    if (this.hasAssignButtonWrapperTarget) {
      this.assignButtonWrapperTarget.classList.toggle('hidden', hasAnyRows);
    }

    if (this.hasActionsWrapperTarget) {
      this.actionsWrapperTarget.classList.toggle('hidden', !showActions);
    }

    if (this.hasAddButtonWrapperTarget) {
      this.addButtonWrapperTarget.classList.toggle('hidden', !showActions);
    }

    if (this.hasRemapButtonWrapperTarget) {
      this.remapButtonWrapperTarget.classList.toggle('hidden', !showRemap);
    }

    this.updateButtonLabel();
  }

  updateButtonLabel() {
    if (!this.hasAddButtonTarget) return;
    this.addButtonTarget.textContent = 'Add transformation';
  }

  refreshRuleKinds() {
    this.ruleRows().forEach((row, index) => {
      const select = row.querySelector("select[name$='[kind]']");
      if (!select) return;

      const useSourceOptions = index === 0;
      const currentValue = select.value;

      const sourceOptions = JSON.parse(select.dataset.rulesSourceOptions || '{}');
      const transformationOptions = JSON.parse(select.dataset.rulesTransformationOptions || '{}');
      const groups = useSourceOptions ? sourceOptions : transformationOptions;
      const allowedValues = Object.values(groups)
        .flat()
        .map(([, value]) => value);

      const alreadyMatches = this.currentGroupsSignature(select) === this.groupsSignature(groups);

      if (!alreadyMatches) {
        this.replaceRuleOptions(select, useSourceOptions, currentValue);
      }

      if (currentValue && !allowedValues.includes(currentValue)) {
        select.value = '';
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  currentGroupsSignature(select) {
    const groups = Array.from(select.querySelectorAll('optgroup')).map((group) => {
      return {
        label: group.label,
        options: Array.from(group.querySelectorAll('option')).map((option) => option.value),
      };
    });

    return JSON.stringify(groups);
  }

  groupsSignature(groups) {
    const normalized = Object.entries(groups).map(([label, options]) => {
      return {
        label,
        options: options.map(([, value]) => value),
      };
    });

    return JSON.stringify(normalized);
  }

  tooltipInfoForKind(kind) {
    return (
      this.descriptionsValue[kind] || {
        title: '',
        text: 'Choose a rule to see what it does.',
      }
    );
  }

  updateDescriptions() {
    this.containerTarget.querySelectorAll('.rule-row').forEach((row) => {
      const select = row.querySelector("select[name$='[kind]']");
      const tooltipWrapper = row.querySelector('.rule-select');

      if (!select || !tooltipWrapper) return;

      const info = this.tooltipInfoForKind(select.value);
      tooltipWrapper.dataset.tooltipTitle = info.title;
      tooltipWrapper.dataset.tooltipText = info.text;
    });
  }

  replaceRuleOptions(select, useSourceOptions, selectedValue = '') {
    const sourceOptions = JSON.parse(select.dataset.rulesSourceOptions || '{}');
    const transformationOptions = JSON.parse(select.dataset.rulesTransformationOptions || '{}');
    const groups = useSourceOptions ? sourceOptions : transformationOptions;

    select.innerHTML = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Choose rule';
    select.appendChild(placeholder);

    Object.entries(groups).forEach(([label, options]) => {
      const optgroup = document.createElement('optgroup');
      optgroup.label = label;

      options.forEach(([text, value]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = text;
        optgroup.appendChild(option);
      });

      select.appendChild(optgroup);
    });

    if (selectedValue) {
      select.value = selectedValue;
    }
  }

  serializeRuleInputs() {
    const fields = Array.from(this.containerTarget.querySelectorAll('input, select, textarea'));

    const pairs = fields
      .filter((field) => field.name && !field.disabled)
      .flatMap((field) => {
        if (field.type === 'checkbox') {
          return [[field.name, field.checked ? '1' : '0']];
        }

        if (field.type === 'radio') {
          return field.checked ? [[field.name, field.value]] : [];
        }

        return [[field.name, field.value]];
      });

    return JSON.stringify(
      pairs.sort(([aKey, aVal], [bKey, bVal]) => {
        if (aKey === bKey) return String(aVal).localeCompare(String(bVal));
        return aKey.localeCompare(bKey);
      })
    );
  }

  announceState() {
    this.element.dispatchEvent(
      new CustomEvent('mapping-row:changed', {
        bubbles: true,
        detail: {
          hasRules: this.realRuleCount() > 0,
          firstRuleSelected: this.firstRuleSelected(),
          dirty: this.isDirty(),
        },
      })
    );
  }
}
